/**
 * The club's waiver, on the account-setup page.
 *
 * An invited or imported member activating their account is the last moment the club
 * is guaranteed to reach them: the entry gate exempts anyone who was already enrolled
 * when the waiver was published, which is every member carried over from the retired
 * Terms system. Asking here is what keeps that cohort from slipping through entirely.
 *
 * Presentational — `useWaiverCapture` on the page owns the fetch, the captured consent
 * and the submit. Same shape as the checkout's `WaiverSection`, including the
 * `waiver-consent-{versionId}` test id the e2e helper drives.
 *
 * @module pages/auth/components/AccountSetupWaiverCard
 */

import { RetryBanner } from '@butter/ui/components/feedback';
import { WaiverConsentCard } from '@butter/ui/domains/legal';
import { butterToast } from '@butter/ui/patterns';
import { downloadBlob } from '@butter/ui/utils';
import { downloadWaiverDocument } from '~/services/internalApi/waiver';
import type { WaiverCapture } from '~/features/waivers';

interface AccountSetupWaiverCardProps {
  clubId: number | null;
  capture: WaiverCapture;
  disabled?: boolean;
}

export function AccountSetupWaiverCard({ clubId, capture, disabled = false }: AccountSetupWaiverCardProps) {
  const handleDownload = async (waiverVersionId: number) => {
    if (!clubId) return;
    try {
      downloadBlob(await downloadWaiverDocument(clubId, waiverVersionId));
    } catch (error) {
      butterToast.error(error instanceof Error ? error.message : 'Failed to download waiver');
    }
  };

  // No skeleton: most clubs publish no waiver, and a placeholder that usually
  // resolves to nothing is worse than a card that appears once loaded.
  if (capture.isLoading) return null;

  // A list we cannot reach is a STATE, not nothing. Setup is fail-closed on the
  // waiver, so rendering null here would leave a disabled "Complete Setup" with no
  // explanation and nothing to click.
  if (capture.answerMissing) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <RetryBanner
          message="We couldn't load this club's waiver, so we can't finish setting up your account yet."
          onRetry={() => capture.retry()}
          testId="waiver-load-failed"
        />
      </div>
    );
  }

  if (capture.waivers.length === 0) return null;

  return (
    <div className="space-y-6" data-testid="account-setup-waiver-section">
      {capture.waivers.map((waiver) => (
        <WaiverConsentCard
          key={waiver.waiverVersionId}
          title={waiver.title}
          contentHtml={waiver.content}
          requireSignature={waiver.requireSignature}
          requireAgreement={waiver.requireAgreement}
          agreementText={waiver.agreementText}
          signature={capture.signatures[waiver.waiverVersionId] ?? null}
          onSignatureChange={(value) => capture.setSignature(waiver.waiverVersionId, value)}
          agreed={capture.agreements[waiver.waiverVersionId] ?? false}
          onAgreedChange={(value) => capture.setAgreement(waiver.waiverVersionId, value)}
          onDownloadPdf={() => handleDownload(waiver.waiverVersionId)}
          disabled={disabled}
          data-testid={`waiver-consent-${waiver.waiverVersionId}`}
        />
      ))}
    </div>
  );
}
